<template>
  <div class="is-loading" v-if="isLoading">
    <b-spinner style="border-width: 2px" />
  </div>
  <div v-else>
    <template v-if="styleType === 'unlimited'">
      <AppTimePicker
        :placeholder="placeholder"
        v-model="time"
        :hasError="hasError"
        @input="handleInput"
        :disabled="disabled"
      />
    </template>
    <template v-else>
      <template v-if="timeStyle == 'select_style'">
        <b-form-select
          class="form-control"
          style="border-width: 1px"
          v-model="time"
          @input="handleInput"
          :disabled="disabled"
        >
          <b-form-select-option :value="null">{{ placeholder }}</b-form-select-option>
          <b-form-select-option
            v-for="timeOption in timeOptions"
            :key="timeOption.value"
            :value="timeOption.value"
            :disabled="optionDisabler(timeOption.value)"
          >
            {{ timeOptionDisplayLabel(timeOption) }}
          </b-form-select-option>
        </b-form-select>
      </template>
      <template v-else-if="timeStyle == 'button_style'">
        <div class="timer">
          <label
            v-for="timeOption in timeOptions"
            :key="timeOption.value"
            class="time-option"
            :class="{ 'selected': time === timeOption.value, 'disabled': optionDisabler(timeOption.value) }"
            @click="pickTime(timeOption.value)"
          >
            <input
              type="radio"
              :id="timeOption.value"
              :value="timeOption.value"
              v-model="time"
              class="time-input"
              :disabled="disabled"
            />
            {{ timeOptionDisplayLabel(timeOption) }}
          </label>
        </div>
      </template>
    </template>
  </div>
</template>

<script>
import AppTimePicker from '@/components/AppTimePicker.vue';

export default {
  props: {
    value: [Date, String],
    placeholder: {
      type: String,
      default: ' ',
    },
    timeStyle: {
      type: String,
      default: 'select_style',
    },
    hasError: {
      type: Boolean,
      default: false,
    },
    styleType: {
      type: String,
      default: 'unlimited',
    },
    startTime: {
      type: String,
      default: '00:00',
    },
    endTime: {
      type: String,
      default: '21:00',
    },
    slotMinutes: {
      type: [String, Number],
      default: "30",
    },
    customTimeOptions: {
      type: Array,
      default: null,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    optionDisabler: {
      type: Function,
      default: (timeOption) => false, // eslint-disable-line
    },
  },
  components: {
    AppTimePicker,
  },
  data: () => ({
    time: null,
  }),
  watch: {
    value() {
      this.time = this.value
    },
  },
  mounted() {
    this.time = this.value
  },
  computed: {
    timeOptions() {
      if (this.styleType === 'specified' && this.customTimeOptions && Array.isArray(this.customTimeOptions)) {
        return this.customTimeOptions
          .filter(option => option.value && option.value.trim() !== '')
          .map(option => ({
            value: option.value,
            text: option.text,
          }));
      }

      if (!this.startTime || !this.endTime) {
        return [];
      }

      let options = [];
      const slotMinutes = Number(this.slotMinutes);
      if (slotMinutes < 1) {
        return [];
      }

      // 將時間轉換為分鐘
      const timeToMinutes = (time) => {
        if (!time) {
          return 0;
        }
        const [hours, minutes] = time.split(':').map(Number);
        return hours * 60 + minutes;
      };

      // 將分鐘轉回時間格式 "HH:MM"
      const minutesToTime = (minutes) => {
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
      };

      const startMinutes = timeToMinutes(this.startTime);
      const endMinutes = timeToMinutes(this.endTime);

      // 計算實際的最後服務時間
      const lastServeMinutes = timeToMinutes(this.endTime);
      const adjustedLastServeMinutes = lastServeMinutes - Number(0);

      // 確保不會超過 24:00
      const actualEndMinutes = Math.min(endMinutes, adjustedLastServeMinutes);

      let currentMinutes = startMinutes;

      while (currentMinutes <= actualEndMinutes) {
        const timeValue = minutesToTime(currentMinutes);
        options.push({ value: timeValue, text: timeValue });
        currentMinutes += slotMinutes;

        // 防止超過一天的範圍（1440 分鐘）
        if (currentMinutes >= 1440) {
          break;
        }
      }

      return options;
    },
  },
  methods: {
    timeOptionDisplayLabel(option) {
      const alias = option.text != null ? String(option.text).trim() : ''
      if (alias !== '') {
        return alias
      }
      return this.formatTimeValueForDisplay(option.value)
    },
    formatTimeValueForDisplay(value) {
      if (value == null || value === '') {
        return ''
      }
      const parts = String(value).split(':')
      if (parts.length < 2) {
        return String(value)
      }
      const h = parts[0].padStart(2, '0')
      const m = parts[1].padStart(2, '0')
      return `${h}:${m}`
    },
    handleInput(value) {
      this.$emit('input', value)
    },
    pickTime(value) {
      if (this.disabled) {
        return
      }
      this.time = value;
      this.handleInput(value)
    }
  },
};
</script>

<style lang="scss" scoped>
@use "@/assets/scss/base-colors";

.timer {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.time-input {
  display: none;
}

.time-option {
  border: 1px solid #E5E5EA;
  width: calc((100% - 16px) / 3);
  text-align: center;
  font-weight: 500;
  padding: 0 30px;
  border-radius: 10px;
  box-shadow: 0 2px 4px 0 rgba(0,0,0,0.1);
  height: 36px;
  line-height: 32px;
  white-space: nowrap;

  &.selected, &.selected.disabled {
    border: 3px solid var(--s-primary, base-colors.$dodger-blue);
  }

  &.disabled {
    border-color: #cdd6dc;
    background-color: #eee;
    color: #888;
    pointer-events: none;
  }
}

.dynamic-select {
  position: relative;
  font-family: "PingFang TC";
  font-style: normal;
  font-weight: 600;
  width: 100%;

  select {
    padding: 0px 1rem;
    -moz-appearance: none; /* Firefox */
    -webkit-appearance: none; /* Safari and Chrome */
    appearance: none;
    font-size: 16px;
    border: solid 1px var(--liff-button-color, #cdd6dc) !important;
    border-radius: 3px;

    .dynamic-select--border & {
      border: 1px solid #636366;
      border-radius: 10px;
      padding: 16px 12px;
      &:focus {
        border-bottom-color: #636366;
      }
    }
  }

  &__caret {
    position: absolute;
    right: 20px;
    width: 12px;
    bottom: 30%;
  }

  &--border {
    select {
      border: 1px solid #636366;
      border-radius: 10px;
      padding: 16px 12px;
      &:focus {
        border-bottom-color: #636366;
      }
    }
  }

  &.invalid {
      border: 1px solid #fe0000;
    }
}

.is-loading {
  height: 45px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: unset;
}
</style>
